import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft,
  User,
  Calendar,
  Clock,
  Building,
  Users,
  Star,
  Mail,
  Phone,
  MapPin,
  Globe,
  BarChart,
  Eye,
  MessageSquare,
  Briefcase,
  GraduationCap,
  FileText,
  Edit,
  Save,
  X,
  Plus,
  Trash2
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Separator } from '@/components/ui/separator';

export default function AdminTalentProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);

  useEffect(() => {
    if (!user || !id) return;
    fetchProfileData();
  }, [user, id]);

  const fetchProfileData = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      // Fetch profile with related data including actual metrics
      const [profileResponse, metricsResponse] = await Promise.all([
        supabase
          .from('profiles')
          .select(`
            *,
            experiences (
              id,
              company_name,
              position,
              start_date,
              end_date,
              current,
              description,
              location
            ),
            education (
              id,
              institution,
              degree,
              field_of_study,
              start_date,
              end_date,
              current,
              description,
              location
            ),
            applications (
              id,
              status,
              application_date,
              recruiting_projects (
                id,
                title,
                profiles (company_name)
              )
            ),
            interviews (
              id,
              title,
              date,
              status
            )
          `)
          .eq('id', id)
          .single(),
        
        supabase
          .from('profile_engagement_metrics')
          .select('*')
          .eq('profile_id', id)
          .maybeSingle()
      ]);

      if (profileResponse.error) throw profileResponse.error;
      const profileData = profileResponse.data;

      // Use actual metrics instead of simulated data
      const stats = {
        totalApplications: profileData.applications?.length || 0,
        totalInterviews: profileData.interviews?.length || 0,
        completedInterviews: profileData.interviews?.filter((i: any) => i.status === 'completed').length || 0,
        profileViews: metricsResponse.data?.total_views || 0,
        uniqueViewers: metricsResponse.data?.unique_viewers || 0,
        applicationViews: metricsResponse.data?.application_views || 0,
        searchViews: metricsResponse.data?.search_views || 0,
        averageRating: calculateAverageRating(profileData),
        lastActive: metricsResponse.data?.last_viewed_at || profileData.updated_at,
        joinDate: profileData.created_at
      };

      // Fetch activity timeline including profile views
      const { data: viewsData } = await supabase
        .from('profile_views')
        .select(`
          id,
          created_at,
          source,
          viewer:profiles!viewer_id(
            id,
            full_name,
            title
          )
        `)
        .eq('profile_id', id)
        .order('created_at', { ascending: false })
        .limit(50);

      // Combine all activities and sort by date
      const activities = [
        ...(profileData.applications || []).map((app: any) => ({
          type: 'application',
          date: app.application_date,
          title: `Applied to ${app.recruiting_projects.title}`,
          company: app.recruiting_projects.profiles.company_name,
          status: app.status
        })),
        ...(profileData.interviews || []).map((interview: any) => ({
          type: 'interview',
          date: interview.date,
          title: interview.title,
          status: interview.status
        })),
        ...(viewsData || []).map((view: any) => ({
          type: 'profile_view',
          date: view.created_at,
          title: 'Profile Viewed',
          viewer: view.viewer,
          source: view.source
        }))
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setProfile(profileData);
      setStats(stats);
      setActivities(activities);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load profile data'
      });
      navigate('/admin/talent');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate average rating
  const calculateAverageRating = (profileData: any) => {
    const ratings = [
      ...(profileData.applications?.map((a: any) => a.rating) || []),
      ...(profileData.interviews?.map((i: any) => i.rating) || [])
    ].filter(Boolean);
    
    if (ratings.length === 0) return 0;
    return (ratings.reduce((sum: number, rating: number) => sum + rating, 0) / ratings.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested profile could not be found or may have been deleted.
          </p>
          <Button onClick={() => navigate('/admin/talent')}>
            Return to Talent Search
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/talent')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Talent Profile</h1>
            <p className="text-muted-foreground">
              Administrative view of user profile and platform activity
            </p>
          </div>
        </div>

        {/* Profile Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-start gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={profile.avatar_url} />
                  <AvatarFallback>{profile.full_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl">{profile.full_name}</CardTitle>
                      <CardDescription>{profile.title}</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={`/profile/${profile.id}`} target="_blank">
                        <Eye className="h-4 w-4 mr-2" />
                        View Public Profile
                      </a>
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mt-4">
                    {profile.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${profile.email}`} className="hover:underline">
                          {profile.email}
                        </a>
                      </div>
                    )}
                    {profile.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{profile.phone}</span>
                      </div>
                    )}
                    {profile.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{profile.location}</span>
                      </div>
                    )}
                    {profile.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {profile.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {profile.summary && (
                <p className="text-muted-foreground whitespace-pre-line">
                  {profile.summary}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Platform Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Platform Activity</CardTitle>
              <CardDescription>User engagement statistics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm text-muted-foreground mb-4">User engagement statistics</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Profile Views</span>
                      <span>{stats.profileViews}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full" 
                        style={{ width: `${Math.min((stats.profileViews / 200) * 100, 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Applications</span>
                    <Badge variant="outline">{stats.totalApplications}</Badge>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Interviews</span>
                    <Badge variant="outline">{stats.totalInterviews}</Badge>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg. Rating</span>
                    <Badge variant="secondary" className="bg-primary/10">
                      <Star className="h-3 w-3 fill-primary text-primary mr-1" />
                      {stats.averageRating}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2 mt-6 pt-6 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Last Active</span>
                    <span>{formatDistanceToNow(new Date(stats.lastActive), { addSuffix: true })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Joined</span>
                    <span>{format(new Date(stats.joinDate), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Experience & Education */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <CardTitle>Work Experience</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {profile.experiences?.map((exp: any) => (
                  <div key={exp.id} className="border-b last:border-0 pb-4 last:pb-0">
                    <h3 className="font-medium">{exp.position}</h3>
                    <p className="text-sm text-muted-foreground">{exp.company_name}</p>
                    <div className="flex items-center gap-2 mt-1 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>
                        {format(new Date(exp.start_date), 'MMM yyyy')} - {' '}
                        {exp.current ? 'Present' : format(new Date(exp.end_date), 'MMM yyyy')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                <CardTitle>Education</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {profile.education?.map((edu: any) => (
                  <div key={edu.id} className="border-b last:border-0 pb-4 last:pb-0">
                    <h3 className="font-medium">{edu.degree}</h3>
                    <p className="text-sm text-muted-foreground">{edu.institution}</p>
                    {edu.field_of_study && (
                      <p className="text-sm">{edu.field_of_study}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Timeline</CardTitle>
            <CardDescription>Recent platform activity and interactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <div key={index} className="flex gap-4">
                  <div className="mt-1">
                    {activity.type === 'application' ? (
                      <Briefcase className="h-5 w-5 text-blue-500" />
                    ) : activity.type === 'interview' ? (
                      <Calendar className="h-5 w-5 text-green-500" />
                    ) : (
                      <Eye className="h-5 w-5 text-purple-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{activity.title}</p>
                      <Badge variant="outline">
                        {format(new Date(activity.date), 'MMM d, yyyy')}
                      </Badge>
                    </div>
                    {activity.company && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {activity.company}
                      </p>
                    )}
                    {activity.viewer && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Viewed by {activity.viewer.full_name}
                        {activity.viewer.title && ` • ${activity.viewer.title}`}
                      </p>
                    )}
                    {activity.status && (
                      <Badge 
                        variant={activity.status === 'completed' ? 'default' : 'secondary'}
                        className="mt-2"
                      >
                        {activity.status}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}